import { getApiUrl } from "../api";
import { useEffect, useState } from "react";
import Preloader from "../component/Preloader";
import Header from "../component/Header";
import LanguagePopup from "../component/LanguagePopup";
import Footer from "../component/Footer";
import FloatingWidgets from "../component/FloatingWidgets";
import HomeModals from "../component/HomeModals";
import useHomeInteractions from "../component/useHomeInteractions";
import { Link } from "../component/AppLink";
import { getCloudflareImageUrl } from "../utils/cloudflareImages";
import { ArabicFooter, ArabicHeader } from "./ArabicHome";

const PAGE_SIZE = 12;

const getJwtToken = () => {
  const keys = ["token", "accessToken", "jwt", "authToken"];
  for (const key of keys) {
    const token = localStorage.getItem(key) || sessionStorage.getItem(key);
    if (token?.trim()) return token.replace(/^Bearer\s+/i, "").trim();
  }

  return (process.env.REACT_APP_JWT_TOKEN || "").replace(/^Bearer\s+/i, "").trim();
};


const stripHtml = (value) => String(value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const formatNewsDate = (value, language = "en") => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return stripHtml(value);
  return date.toLocaleDateString(language === "ar" ? "ar-AE" : "en-US", { year: "numeric", month: "short", day: "2-digit" });
};

const mapNews = (item, index, language = "en") => {
  const isArabic = language === "ar";
  const imagePath = item.ImagePath || item.image || item.imagePath || item.icon || "";
  return {
    id: item.id || item.Id || `news-${index}`,
    title: stripHtml((isArabic ? (item.title_ar || item.Title_ar || item.titleAr || item.TitleAr) : null) || item.title_en || item.Title_en || item.title || item.Title),
    description: stripHtml((isArabic ? (item.description_ar || item.Description_ar || item.descriptionAr || item.DescriptionAr) : null) || item.description_en || item.Description_en || item.description || item.Description),
    date: formatNewsDate(item.dt || item.date, language),
    image: getCloudflareImageUrl(imagePath, "w=700,sharpen=1"),
  };
};

export default function News({ language = "en" }) {
  const isArabic = language === "ar";
  const interactions = useHomeInteractions();
  const [newsList, setNewsList] = useState([]);
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const hasMore = totalRecords > newsList.length;

  useEffect(() => {
    document.title = isArabic ? "أخبار تراحم | مؤسسة تراحم الخيرية" : "Tarahum News | Tarahum Charity Foundation";
    if (!isArabic) return undefined;
    document.documentElement.lang = "ar";
    document.documentElement.dir = "rtl";
    document.body.classList.add("arabic-layout-page");
    return () => {
      document.documentElement.lang = "en";
      document.documentElement.dir = "ltr";
      document.body.classList.remove("arabic-layout-page");
    };
  }, [isArabic]);

  useEffect(() => {
    const controller = new AbortController();

    const loadNews = async () => {
      setIsLoading(true);
      try {
        setLoadError("");
        const headers = { Accept: "application/json" };
        const token = getJwtToken();
        if (token) headers.Authorization = `Bearer ${token}`;

        const response = await fetch(getApiUrl(`/api/GetNewsList.ashx?page=${page}&pageSize=${PAGE_SIZE}`), {
          headers,
          credentials: "include",
          signal: controller.signal,
        });

        if (!response.ok) throw new Error(`News request failed with ${response.status}`);

        const result = await response.json();
        const rows = Array.isArray(result?.data) ? result.data : [];
        const mappedNews = rows.map((item, index) => mapNews(item, index, language)).filter((item) => item.title);

        setTotalRecords(Number(result?.totalRecords) || mappedNews.length);
        setNewsList((current) => (page === 1 ? mappedNews : [...current, ...mappedNews]));
      } catch (error) {
        if (error.name !== "AbortError") {
          console.warn("Unable to load news", error);
          setLoadError(isArabic ? "تعذر تحميل الأخبار حالياً. يرجى المحاولة لاحقاً." : "Unable to load news right now. Please try again later.");
          if (page === 1) {
            setNewsList([]);
            setTotalRecords(0);
          }
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    loadNews();

    return () => controller.abort();
  }, [page, isArabic, language]);

  return (
    <>
      <Preloader />
      {isArabic ? <ArabicHeader /> : <><Header /><LanguagePopup /></>}

      <section className="kid-d-wrp pb-0">
        <div className="kid-container">
          <div className="kid-hero">
            <h1>{isArabic ? "أخبار تراحم" : "Tarahum News"}</h1>
            <ul className="kid-breadcrumb">
              <li><Link to="/">{isArabic ? "الرئيسية" : "home"}</Link></li>
              <li>/</li>
              <li>{isArabic ? "أخبار تراحم" : "Tarahum News"}</li>
            </ul>
          </div>
        </div>
      </section>

      <div className={`our-blog news-w${isArabic ? " arabic-news-page" : ""}`} dir={isArabic ? "rtl" : undefined}>
        <div className="container">
          {loadError ? (
            <div className="donation-error">{loadError}</div>
          ) : null}

          {!isLoading && !newsList.length && !loadError ? (
            <div className="donation-error">{isArabic ? "لا توجد أخبار متاحة حالياً." : "No news records are available right now."}</div>
          ) : null}

          <div className="row">
            {newsList.map((item, index) => {
              const detailUrl = `/news-details?id=${encodeURIComponent(item.id || "")}`;

              return (
                <div className="col-xl-4 col-md-6" key={item.id || index}>
                  <div className="post-item wow fadeInUp" data-wow-delay={index % 3 === 1 ? "0.2s" : index % 3 === 2 ? "0.4s" : undefined}>
                    <div className="post-featured-image">
                      <Link to={detailUrl} data-cursor-text="View">
                        <figure>
                          <img src={item.image} alt={item.title} />
                        </figure>
                      </Link>
                    </div>
                    <div className="post-item-body">
                      <div className="post-item-content">
                        {item.date ? <span className="news-date">{item.date}</span> : null}
                        <h2>
                          <Link to={detailUrl}>
                            {item.title}
                          </Link>
                        </h2>
                      </div>
                      <div className="post-item-btn">
                        <Link to={detailUrl} className="readmore-btn">
                          {isArabic ? "اقرأ المزيد" : "read more"}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {isLoading && !newsList.length ? (
            <div className="text-center mt-4">
              <button className="btn-default" type="button" disabled>
                {isArabic ? "جارٍ التحميل..." : "Loading..."}
              </button>
            </div>
          ) : null}

          {hasMore ? (
            <div className="text-center mt-4">
              <button className="btn-default" type="button" onClick={() => setPage((current) => current + 1)} disabled={isLoading}>
                {isLoading ? (isArabic ? "جارٍ التحميل..." : "Loading...") : (isArabic ? "تحميل المزيد" : "Load More")}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {isArabic ? <ArabicFooter /> : <Footer />}
      <FloatingWidgets {...interactions} />
      <HomeModals {...interactions} />
    </>
  );
}
